'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { PAGES_NOT_FETCH_API } from '~/core/constants/url'
import useContextGraphQL from '~/core/middleware/use-context-graphQL'
import { catchErrorFromGraphQL } from '~/core/utilities/catch-api-error'

import QueryNotificationsUnreadCount from '~/lib/features/notifications/graphql/query-notifications-unread-count'
import useNotificationStore from '~/lib/features/notifications/store'
import { PLAN_FEATURE_KEYS } from '~/lib/features/settings/plans/utilities/enum'
import { useRouterContext } from '~/lib/next/use-router-context'
import useBoundStore from '~/lib/store'
import useToastStore from '~/lib/store/toast'

import useSubscriptionPlan from '../Subscription/useSubscriptionPlan'

const NotificationBadgeCount = () => {
  const { pathnameWithoutLocale } = useRouterContext()
  const { clientGraphQL } = useContextGraphQL()
  const { user } = useBoundStore()
  const { setToast } = useToastStore()
  const { notificationTotalUnreadCount, setNotificationTotalUnreadCount } = useNotificationStore()
  const { isFeatureEnabled, isUnLockFeature, data: dataPlan } = useSubscriptionPlan()

  const [isFirstLoading, setFirstLoading] = useState(true)

  const fetchNotificationBadgeCount = useCallback(() => {
    clientGraphQL.query(QueryNotificationsUnreadCount).then(
      (result: {
        error: { graphQLErrors: Array<object> }
        data: {
          notificationsUnreadCount: number
        }
      }) => {
        if (result.error) {
          return catchErrorFromGraphQL({
            error: result.error,
            setToast
          })
        }

        const { notificationsUnreadCount } = result.data || {}
        const count =
          notificationTotalUnreadCount !== notificationsUnreadCount
            ? notificationsUnreadCount >= 0
              ? notificationsUnreadCount
              : 0
            : 0

        if (notificationsUnreadCount !== notificationTotalUnreadCount) {
          setNotificationTotalUnreadCount(count)
        }

        return
      }
    )
  }, [clientGraphQL, notificationTotalUnreadCount])

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Clear existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (
      !PAGES_NOT_FETCH_API.some((url) => pathnameWithoutLocale?.startsWith(url)) &&
      user?.id &&
      isFeatureEnabled(PLAN_FEATURE_KEYS.notification) &&
      isUnLockFeature(PLAN_FEATURE_KEYS.notification)
    ) {
      // Fetch on first load and request permission
      if (isFirstLoading) {
        const retrieveNotificationCount = async () => {
          try {
            if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
              await Notification.requestPermission()
            }
          } catch (error) {
            console.error(error)
          }
        }

        setFirstLoading(false)
        fetchNotificationBadgeCount()
        retrieveNotificationCount()
      }

      // ✅ FIX: Only poll every 30s when permission is 'denied'
      // If permission is 'granted' or 'default', Firebase realtime will handle updates
      const permission = Notification.permission

      if (permission === 'denied') {
        intervalRef.current = setInterval(() => {
          fetchNotificationBadgeCount()
        }, 30000)
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [
    user,
    dataPlan,
    pathnameWithoutLocale,
    fetchNotificationBadgeCount,
    isFirstLoading,
    isFeatureEnabled,
    isUnLockFeature
  ])

  return null
}

export default NotificationBadgeCount
